import React, { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import ListAltIcon from '@mui/icons-material/ListAlt'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import RateReviewIcon from '@mui/icons-material/RateReviewOutlined'
import TierSection, { TierVariant } from '../components/dashboard/TierSection'
import { groupFrameworkTiers } from '../utils/frameworkTiers'
import useRouter from '../hooks/useRouter'
import useAccount from '../hooks/useAccount'
import useFrameworkLibrary, { AutoCoderFrameworkLibrary } from '../hooks/useFrameworkLibrary'
import DeleteConfirmWindow from '../components/system/DeleteConfirmWindow'
import PageAgentButton from '../agent/components/PageAgentButton'
import PageAgentDrawer from '../agent/components/PageAgentDrawer'
import { useVoiceRegistration } from '../voice/useVoiceRegistration'
import { buildVoiceTools, voiceCollection } from '../voice/buildVoiceTools'
import { VoiceUITool } from '../voice/types'
import { useDisplayNames } from '../utils/displayNames'

interface FrameworkLibraryViewProps {
  // Mounted inside the AutoCoder shell's Code Frames tab: the shell owns the
  // viewport height, and "Pipelines" switches tab instead of navigating away.
  embedded?: boolean
  onViewPipelines?: () => void
}

export const FrameworkLibraryView: React.FC<FrameworkLibraryViewProps> = ({
  embedded = false,
  onViewPipelines,
}) => {
  const router = useRouter()
  const account = useAccount()
  const [deleteTarget, setDeleteTarget] = useState<AutoCoderFrameworkLibrary | null>(null)
  // The page helper (Douglas plan T16).
  const [helperOpen, setHelperOpen] = useState(false)

  const customer = router.params.customer || account.customer?.name || ''
  const job = router.params.job || account.job?.name || ''

  const {
    frameworks,
    frameworksLoading,
    frameworksError,
    deleteFramework,
    deleteLoading,
    setFrameworkVisibility,
  } = useFrameworkLibrary({ customer, job })
  const userEmail = account.user?.session_info?.email || account.user?.session_info?.name || ''
  const canPromote = Boolean(account.user?.role_permissions?.isManager)
  // Data Viz's three tiers (Adam, 23 Sep: "the same three tiers as data").
  const grouped = groupFrameworkTiers(frameworks, userEmail)
  const tiers: { variant: TierVariant; title: string; emptyLabel: string; frames: AutoCoderFrameworkLibrary[] }[] = [
    { variant: 'company', title: 'Company-level', emptyLabel: 'No company-level code frames yet', frames: grouped.company },
    { variant: 'mine', title: 'My code frames', emptyLabel: "You haven't saved any code frames for this job yet", frames: grouped.mine },
    { variant: 'colleagues', title: "My colleagues' code frames", emptyLabel: 'No colleagues have code frames in this job yet', frames: grouped.colleagues },
  ]
  const [openTiers, setOpenTiers] = useState<Partial<Record<TierVariant, boolean>>>({})
  const isTierOpen = (variant: TierVariant, count: number) => openTiers[variant] ?? count > 0

  const handleViewPipelines = () => {
    if (onViewPipelines) {
      onViewPipelines()
      return
    }
    if (!customer || !job) return
    router.navigate('autocoder', { customer, job })
  }

  const handleNewManual = () => {
    if (!customer || !job) return
    router.navigate('framework-editor', {
      customer,
      job,
      frameworkId: 'new-manual',
    })
  }

  const handleEditClick = (framework: AutoCoderFrameworkLibrary) => {
    if (!customer || !job) return
    router.navigate('framework-editor', {
      customer,
      job,
      frameworkId: framework.id,
    })
  }

  // A dynamic frame's suggestions (docs/autocoder-dynamic-frames.md).
  const handleReviewSuggestions = (framework: AutoCoderFrameworkLibrary) => {
    if (!customer || !job) return
    router.navigate('framework-suggestions', { customer, job, frameworkId: framework.id })
  }

  const handleDeleteClick = (framework: AutoCoderFrameworkLibrary) => {
    setDeleteTarget(framework)
  }

  const handleConfirmDelete = () => {
    if (!deleteTarget || deleteLoading) return
    deleteFramework(deleteTarget.id)
    setDeleteTarget(null)
  }

  const handleCancelDelete = () => {
    if (deleteLoading) return
    setDeleteTarget(null)
  }

  // Confirm-gated delete: the model must call once to get the confirmation
  // message, then call again with confirmed=true (after the user agrees) to
  // actually delete. Built inline rather than via voiceCollection's `costly`
  // flag because that flag's wording is about AI-token spend, which doesn't
  // fit a destructive delete — mirrors the confirm-first pattern used by the
  // donor's notify_colleague tool (ask, then act only once confirmed).
  const deleteFrameworkVoiceTool: VoiceUITool = {
    name: 'delete_framework',
    description:
      'Delete a code frame, identified by the id from find_framework. Destructive and irreversible — always confirm with the user first: call once without confirmed to get a confirmation message, read it back to the user, and only call again with confirmed=true once they say yes.',
    meta: { category: 'item-action', collection: 'framework', confirm: 'click' },
    parameters: {
      type: 'object',
      properties: {
        id: { type: 'string', description: 'The code frame id (from find_framework).' },
        confirmed: { type: 'boolean', description: 'Set true only after the user has explicitly confirmed the deletion.' },
      },
      required: ['id'],
      additionalProperties: false,
    },
    execute: (args) => {
      const id = String(args.id ?? '')
      const framework = frameworks.find(f => f.id === id)
      if (!framework) {
        return JSON.stringify({ error: `no code frame with id "${id}" — call find_framework first` })
      }
      if (args.confirmed !== true) {
        return JSON.stringify({
          needs_confirmation: true,
          message: `About to delete "${framework.name || framework.id}" — say yes to confirm`,
        })
      }
      deleteFramework(framework.id)
      return JSON.stringify({ ok: true, did: 'delete', id: framework.id })
    },
  }

  // Douglas reads this back aloud, so it carries display names rather than

  // the codes in the URL (#1347 item 1).

  const names = useDisplayNames(customer)


  useVoiceRegistration({
    pageKey: `frameworks:${customer}:${job}`,
    pageContext: () =>
      `Code Frame Library page for customer ${names.customer}, job ${names.job(job)}. ${frameworks.length} code frame(s) listed. ` +
      `Available actions: create a new code frame, open the AutoCoder pipelines, find/edit/delete a code frame.`,
    uiTools: [
      ...buildVoiceTools({
        actions: [
          {
            name: 'create_new_code_frame',
            label: 'create a new code frame',
            run: () => handleNewManual(),
          },
          {
            name: 'view_pipelines',
            label: 'open the AutoCoder pipelines',
            run: () => handleViewPipelines(),
          },
        ],
        collections: [
          voiceCollection<AutoCoderFrameworkLibrary>({
            name: 'framework',
            label: 'framework',
            list: () => frameworks,
            identify: (f) => ({ id: f.id, label: f.name || f.id }),
            actions: [
              { name: 'edit_framework', label: 'edit', run: (f) => handleEditClick(f) },
            ],
          }),
        ],
      }),
      deleteFrameworkVoiceTool,
    ],
  })

  // Helper to format date
  const formatDate = (timestamp: number): string => {
    if (!timestamp) return '-'
    const date = new Date(timestamp * 1000)
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  }

  // Helper to get code count display
  const getCodesCount = (framework: AutoCoderFrameworkLibrary): string => {
    const count = framework.data?.main_variable?.codes?.length || 0
    return count > 0 ? `${count} codes` : '-'
  }

  const renderLabels = (labels: Record<string, string> | undefined) => {
    if (!labels || Object.keys(labels).length === 0) {
      return <Typography variant="caption" color="text.secondary">-</Typography>
    }

    return (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {Object.entries(labels).map(([key, value]) => (
          <Chip
            key={key}
            label={`${key}:${value}`}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        ))}
      </Box>
    )
  }

  const renderRows = (list: AutoCoderFrameworkLibrary[]) => {
    return list.map(framework => {
      return (
        // The row is keyed on the code frame's id, never its name: `name` is
        // optional here (the cell falls back to "(Unnamed)") and two frames may
        // share one, so the id is the only thing that identifies a row.
        <TableRow key={framework.id} hover data-testid={`autocoder-framework-row-${framework.id}`}>
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" fontWeight={500} data-testid={`autocoder-framework-name-${framework.id}`}>
                {framework.name || '(Unnamed)'}
              </Typography>
              {framework.dynamic && (
                <Tooltip title="Dynamic: collects the model's suggestions for new codes. Click to review them.">
                  <Chip
                    size="small"
                    color="info"
                    variant="outlined"
                    data-testid={`autocoder-framework-dynamic-${framework.id}`}
                    label={framework.pending_suggestions ? `Dynamic · ${framework.pending_suggestions} pending` : 'Dynamic'}
                    onClick={() => handleReviewSuggestions(framework)}
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Tooltip>
              )}
            </Box>
            {framework.description && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {framework.description.length > 60 ? framework.description.slice(0, 60) + '...' : framework.description}
              </Typography>
            )}
          </TableCell>

          <TableCell>
            <Typography variant="body2" data-testid={`autocoder-framework-codes-${framework.id}`}>
              {getCodesCount(framework)}
            </Typography>
          </TableCell>

          <TableCell>
            <Typography variant="body2">
              {framework.created_by || '-'}
            </Typography>
          </TableCell>

          <TableCell>
            <Typography variant="body2">
              {formatDate(framework.created_at)}
            </Typography>
          </TableCell>

          <TableCell data-testid={`autocoder-framework-source-${framework.id}`}>
            {framework.source_pipeline_id ? (
              <Chip
                icon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                label="ThemeAI"
                size="small"
                color="secondary"
                variant="outlined"
                sx={{ height: 22 }}
              />
            ) : (
              <Chip
                label="Manual"
                size="small"
                variant="outlined"
                sx={{ height: 22 }}
              />
            )}
          </TableCell>

          <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
            {canPromote && (
              <Tooltip title={framework.visibility === 'company' ? 'Demote from company-level' : 'Promote to company-level'}>
                <IconButton
                  aria-label={framework.visibility === 'company' ? `Demote ${framework.name} from company-level` : `Promote ${framework.name} to company-level`}
                  onClick={() => setFrameworkVisibility(framework, framework.visibility === 'company' ? 'user' : 'company')}
                >
                  {framework.visibility === 'company' ? <StarIcon sx={{ color: 'warning.main' }} /> : <StarBorderIcon />}
                </IconButton>
              </Tooltip>
            )}
            {framework.dynamic && (
              <Tooltip title="Review suggestions">
                <IconButton
                  aria-label='Review suggestions'
                  data-testid={`autocoder-framework-suggestions-${framework.id}`}
                  onClick={() => handleReviewSuggestions(framework)}
                >
                  <RateReviewIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Edit Code Frame">
              <IconButton
                aria-label='Edit Code Frame'
                data-testid={`autocoder-framework-edit-${framework.id}`}
                onClick={() => handleEditClick(framework)}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Delete Code Frame">
              <IconButton
                aria-label='Delete Code Frame'
                data-testid={`autocoder-framework-delete-${framework.id}`}
                onClick={() => handleDeleteClick(framework)}
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </TableCell>
        </TableRow>
      )
    })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: embedded ? '100%' : 'calc(100vh - 84px)', p: 3 }}>
      <Paper elevation={1} sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant='h5' sx={{ mb: 1 }}>Code Frame Library</Typography>
          <Typography variant='body2' color='text.secondary'>
            Create and manage reusable code frames for {customer}/{job}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {!embedded && (
            <PageAgentButton
              pageKey="framework-library"
              customer={account.customer?.name || customer}
              open={helperOpen}
              onClick={() => setHelperOpen(o => !o)}
            />
          )}
          <Button
            variant='outlined'
            color='info'
            size='large'
            startIcon={<ListAltIcon />}
            data-testid='autocoder-frameworks-view-pipelines'
            onClick={handleViewPipelines}
            disabled={!customer || !job}
          >
            Pipelines
          </Button>
          <Button
            variant='contained'
            color='info'
            size='large'
            startIcon={<AddIcon />}
            data-testid='autocoder-frameworks-new'
            onClick={handleNewManual}
            disabled={!customer || !job}
          >
            New Code Frame
          </Button>
        </Box>
      </Paper>

      <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {frameworksLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
        ) : frameworksError ? (
          <Alert severity='error'>{frameworksError instanceof Error ? frameworksError.message : 'Unable to load frameworks.'}</Alert>
        ) : !frameworks.length ? (
          <Typography variant='body2' color='text.secondary' data-testid='autocoder-frameworks-empty' sx={{ textAlign: 'center', py: 4 }}>
            No code frames yet. Click "New Code Frame" to create your first code frame.
          </Typography>
        ) : (
          tiers.map((tier) => (
            <TierSection
              key={tier.variant}
              title={tier.title}
              count={tier.frames.length}
              open={isTierOpen(tier.variant, tier.frames.length)}
              onToggle={() => setOpenTiers({ ...openTiers, [tier.variant]: !isTierOpen(tier.variant, tier.frames.length) })}
              variant={tier.variant}
            >
              {tier.frames.length === 0 ? (
                <Typography variant='body2' color='text.secondary' sx={{ py: 1.5, px: 1 }}>{tier.emptyLabel}</Typography>
              ) : (
                <TableContainer component={Paper} elevation={0}>
                  <Table size='small' data-testid={`autocoder-frameworks-table-${tier.variant}`}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Codes</TableCell>
                        <TableCell>Created By</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Source</TableCell>
                        <TableCell align='right'>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>{renderRows(tier.frames)}</TableBody>
                  </Table>
                </TableContainer>
              )}
            </TierSection>
          ))
        )}
      </Box>

      <DeleteConfirmWindow
        open={Boolean(deleteTarget)}
        title='Delete Code Frame'
        message={`Are you sure you want to delete "${deleteTarget?.name || deleteTarget?.id}"? This action cannot be undone.`}
        confirmWord='delete'
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {!embedded && (
        <PageAgentDrawer
          pageKey="framework-library"
          customer={account.customer?.name || customer}
          job={job}
          open={helperOpen}
          onClose={() => setHelperOpen(false)}
        />
      )}
    </Box>
  )
}

/**
 * The standalone `/frameworks/:customer/:job` route is now a forwarder: the
 * library lives in the AutoCoder shell's Code Frames tab. The route stays
 * registered so old links (and FrameworkEditor's "back") keep working.
 */
const FrameworkLibraryPage: React.FC = () => {
  const router = useRouter()
  const account = useAccount()

  const customer = router.params.customer || account.customer?.name || ''
  const job = router.params.job || account.job?.name || ''

  // Navigation is a side effect, so it cannot happen during render. The deps
  // are the trigger: land on this route (or change job) -> forward once.
  useEffect(() => {
    if (!customer || !job) return
    router.navigate('autocoder', { customer, job, tab: 'frameworks' })
  }, [customer, job])

  return null
}

export default FrameworkLibraryPage
